import { cloneDeep, uniqBy } from "lodash"
import { findObjectByPathValue, roundNumber } from "../utils"
import { getDefaultStepComponents, getSupplierItemPieceUnitWeight, recalculateCostValues, updateAllRecipesFieldsOnChange, updatePriorStepsInUlteriorComponents } from "./recipes"
import { convertKilosIntoGrams } from "./ressources"

/**
 * Get data to display for a step component in the prior steps of a production step execution.
 * @returns 
 */
export const getPSEStepComponentsData = ({
  stepComponent,
  stepComponentPositionIndex,
  expectedProduction,
  coeff,
  productionStepExecution,
  priorStepData }) => {
  let weight = roundNumber(stepComponent.grossWeight * expectedProduction * coeff, 3)
  let supplierItemPieceUnitWeight = getSupplierItemPieceUnitWeight(stepComponent, stepComponent?.supplierItem)

  if (productionStepExecution.mergedProductionStepExecutions && productionStepExecution.mergedProductionStepExecutions.length > 0) {
    weight = productionStepExecution.mergedProductionStepExecutions.reduce((acc, mergedPSE) => {
      supplierItemPieceUnitWeight = 0
      // const mergedStepComponent = mergedPSE.productionStepSnapshot?.stepComponents.find(mergedStepComponent => mergedStepComponent.index === stepComponent.index)
      const mergedStepComponent = (mergedPSE.productionStepSnapshot?.stepComponents || [])[stepComponentPositionIndex]
      // console.log("MERGED STEP COMPONENT", mergedStepComponent, mergedPSE)
      if (mergedStepComponent) {
        const currentSupplierItemPieceUnitWeight = getSupplierItemPieceUnitWeight(mergedStepComponent, stepComponent?.supplierItem)
        if (currentSupplierItemPieceUnitWeight) {
          supplierItemPieceUnitWeight += currentSupplierItemPieceUnitWeight
        }
        return acc + roundNumber(mergedStepComponent.grossWeight * mergedPSE.expectedProduction * mergedPSE.coeff, 3)
      }
      return acc
    }, 0)
  }

  let gap = ""

  if (stepComponent.priorSteps && stepComponent.priorSteps.index && priorStepData) {
    const currentPriorStepData = priorStepData.find(priorStep => priorStep.stepIndex === stepComponent.priorSteps.index)
    if (currentPriorStepData) {
      // If the prior step was reproded separately, its coeff may differ from the current step one.
      // We recompute grossWeight using that coeff to get accurate gap and grossWeight.
      if (currentPriorStepData.coeff !== undefined && currentPriorStepData.coeff !== coeff && !!currentPriorStepData.original) {
        weight = roundNumber(stepComponent.grossWeight * expectedProduction * currentPriorStepData.coeff, 3)
      }

      gap = roundNumber(currentPriorStepData.realNetWeight - weight, 3)
      weight = currentPriorStepData.realNetWeight
    }
  }

  if (stepComponent.sectionOutput) {
    const realNetWeight = productionStepExecution &&productionStepExecution.sectionOutputRealNetWeight && productionStepExecution.sectionOutputRealNetWeight[stepComponent.sectionOutput.index] || 0
    if (realNetWeight) {
      gap = roundNumber(realNetWeight - weight, 3)
      weight = realNetWeight
    }
  }

  return {
    gap,
    weight,
    supplierItemPieceUnitWeight,
  }
}

const getPSEStepComponentObjBySupplierItem = (productionStepExecution, supplierItemId) => {
  const productionStepSnapshot = productionStepExecution.productionStepSnapshot || productionStepExecution.get("productionStepSnapshot")

  const currentIngredient = (productionStepSnapshot.stepComponents || []).find((stepComponent) => {
    if (stepComponent.supplierItem) {
      return stepComponent.supplierItem.id === supplierItemId || stepComponent.supplierItem.objectId === supplierItemId
    }

    if (stepComponent.priorSteps && stepComponent.priorSteps.supplierItem) {
      return stepComponent.priorSteps.supplierItem.id === supplierItemId || stepComponent.priorSteps.supplierItem.objectId === supplierItemId
    }

    return false
  })

  return currentIngredient
}

const getPSEIngredientWeight = (productionStepExecution, supplierItemId) => {
  const substitutes = typeof productionStepExecution.get === "function"
    ? productionStepExecution.get("substitutes") || []
    : productionStepExecution.substitutes || []
  const substituteObj = substitutes.find((substitute) => (substitute.replacementSupplierItem?.id || substitute.replacementSupplierItem?.objectId) === supplierItemId)

  let currentSupplierItemId = supplierItemId

  if (substituteObj && substituteObj.originalSupplierItem) {
    currentSupplierItemId = substituteObj.originalSupplierItem.id || substituteObj.originalSupplierItem.objectId
  }

  const ingredient = getPSEStepComponentObjBySupplierItem(productionStepExecution, currentSupplierItemId)

  if (!ingredient) {
    return 0
  }

  const grossWeight = ingredient.grossWeight || 0
  const expectedProduction = productionStepExecution.expectedProduction || productionStepExecution.get("expectedProduction") || 0
  const coeff = productionStepExecution.coeff || productionStepExecution.get("coeff") || 1

  const weight = grossWeight * expectedProduction * coeff
  return roundNumber(weight)
}

/**
 * Get the unpacking progress from prior PSEs for a given production step execution.
 * @param {*} productionStepExecution 
 * @returns 
 */
export const getPriorPSEsUnpackingProgress = (productionStepExecution) => {
  const ingredients = []
  const priorSteps = productionStepExecution.priorSteps || []
  for (const priorStep of priorSteps) {
    const priorPSE = priorStep.step || {}
    if (priorPSE.productionStepSnapshot.transformation === "UNPACKING") {
      const unpackingProgress = priorPSE.unpackingProgress || []
      for (const ingredient of unpackingProgress) {
        const supplierItemId = ingredient.supplierItem.objectId

        const index = priorPSE.objectId + " - " + supplierItemId
        // Check if the ingredient is already in the newStepComponents
        const supplierItemPieceUnitWeight = getSupplierItemPieceUnitWeight(priorPSE.productionStepSnapshot, ingredient?.supplierItem)
        if (!ingredients.some(item => item.index === index)) {
          ingredients.push({
            name: ingredient.supplierItem.name,
            weight: getPSEIngredientWeight(priorPSE, supplierItemId),
            supplierItemPieceUnitWeight,
            index,
          })
        }
      }
    }
  }

  return ingredients
}

/**
 * Get the name of a PSE step component, considering substitutes.
 * @param {*} stepComponent 
 * @param {*} substitutes 
 * @returns 
 */
const getPSEStepComponentName = (stepComponent, substitutes = []) => {
  let name = ""
  if (stepComponent.sectionOutput) {
    //@TODO mode retrocompatibility: to remove when all recipes are migrated
    const percentage = stepComponent.sectionOutput.percentage || stepComponent.sectionOutput.name.includes("%") ? stepComponent.sectionOutput.name.match(/(\d+)%/)[1] : 0
    const sectionName = (stepComponent.sectionOutput.type === "percentage") ? `${stepComponent.sectionOutput.sectionName} (${percentage}%)` : stepComponent.sectionOutput.realName
    name = `${sectionName} - ${roundNumber(convertKilosIntoGrams(stepComponent.netWeight), 2) + "g"}`
  }

  if (stepComponent.priorSteps) {
    name = stepComponent.priorSteps.name
  }

  if (stepComponent.supplierItem) {
    const replacementSupplierItem = substitutes.find(substitute => substitute.originalSupplierItem.objectId === stepComponent.supplierItem.objectId)?.replacementSupplierItem
    name = replacementSupplierItem ? replacementSupplierItem.name : stepComponent.supplierItem.name
  }
  return name
}

/**
 * Used for PSE status other than "DONE" to get the ingredients data for the step components table.
 */
export const getPSEStepComponentIngredientsData = ({
  stepComponents = [],
  substituteSupplierItems = [],
  expectedProduction,
  coeff,
  productionStepExecution,
  priorStepData
}) => {
  const newStepComponents = []
  for (const stepComponent of stepComponents) {
    const stepComponentPositionIndex = stepComponents.indexOf(stepComponent)
    if (stepComponent.priorSteps?.transformation === "UNPACKING") {
      const unpackingIngredients = getPriorPSEsUnpackingProgress(productionStepExecution)
      newStepComponents.push(...unpackingIngredients)
    } else {
      const { weight, supplierItemPieceUnitWeight, gap } = getPSEStepComponentsData({
        stepComponent,
        stepComponentPositionIndex,
        expectedProduction,
        coeff,
        productionStepExecution,
        priorStepData,
      })

      newStepComponents.push({
        name: getPSEStepComponentName(stepComponent, substituteSupplierItems),
        weight,
        supplierItemPieceUnitWeight,
        gap,
        index: stepComponent.index
      })
    }
  }

  return uniqBy(newStepComponents, "index")
}

const _getSupplierItemObjectIdWithSubstitutes = (stepComponent, substitutes = []) => {
  if (!stepComponent.supplierItem) {
    return ""
  }
  const replacementSupplierItem = substitutes.find(substitute => substitute.originalSupplierItem.objectId === stepComponent.supplierItem.objectId)?.replacementSupplierItem
  if (replacementSupplierItem) {
    return replacementSupplierItem.objectId
  }
  return stepComponent.supplierItem.objectId
}

/**
 * Used only for PSE status "DONE" to get the ingredients data for the lots table.
 */
export const getPSELotStepComponentIngredientsData = ({
  productionStepExecution,
  priorStepData,
  secondaryDLCsBySupplierItems
}) => {
  const stepComponents = productionStepExecution?.productionStepSnapshot.stepComponents || []
  const newStepComponents = []
  for (const stepComponent of stepComponents) {
    let weight = roundNumber(stepComponent.grossWeight * (productionStepExecution.expectedProduction) * (productionStepExecution.coeff || 1), 3)
    let gap = ""

    if (stepComponent.priorSteps?.transformation === "UNPACKING") {
      const unpackingIngredients = getPriorPSEsUnpackingProgress(productionStepExecution)
      newStepComponents.push(...unpackingIngredients)
    } else {
      if (stepComponent.priorSteps?.index && priorStepData) {
        const currentPriorStepData = priorStepData.find(priorStep => priorStep.stepIndex === stepComponent.priorSteps.index)
        if (currentPriorStepData) {
          gap = roundNumber(currentPriorStepData.realNetWeight - weight, 3)
          weight = currentPriorStepData.realNetWeight
        }
      }

      const supplierItemObjectId = _getSupplierItemObjectIdWithSubstitutes(stepComponent, productionStepExecution.substitutes)

      const isPriorSteps = !stepComponent.supplierItem
      const hasSecondaryDLC = isPriorSteps ? false : (secondaryDLCsBySupplierItems.get(supplierItemObjectId) || []).length !== 0
      newStepComponents.push({
        name: getPSEStepComponentName(stepComponent, productionStepExecution.substitutes),
        index: stepComponent.index,
        gap,
        weight,
        hasSecondaryDLC
      })
    }
  }

  return uniqBy(newStepComponents, "index")
}



export const removeStepComponent = (recipe, stepComponentToDelete, setFieldValue, defaultStepComponents, options = { fromRecipe: true }) => {
  const { fromRecipe } = options
  let isPseudoDeletion = false
  const emptyStepComponent = getDefaultStepComponents()
  const path = fromRecipe ? "sections.productionSteps.step.stepComponents.index" : "productionSteps.stepComponents.index"
  const [stepComponentInRecipe] = findObjectByPathValue(recipe, path, stepComponentToDelete.index, { path: true, relative: !fromRecipe })
  const [componentIndex = options.index, indexStep = options.indexStep, sectionIndex = options.sectionIndex] = stepComponentInRecipe ? stepComponentInRecipe.path.match(/\d+/g).map(Number) : []
  const section = options.section ?? recipe.sections[sectionIndex]
  const parentStep = options.parentStep ?? recipe.sections[sectionIndex].productionSteps[indexStep].step
  const steps = options.steps ?? recipe.sections[sectionIndex].productionSteps
  const stepComponents = defaultStepComponents ?? parentStep.stepComponents

  stepComponents.splice(componentIndex, 1)
  if (!stepComponents.length) {
    stepComponents.splice(0, 0, emptyStepComponent)
    isPseudoDeletion = true
  }
  if (fromRecipe) {
    setFieldValue(`sections[${sectionIndex}].productionSteps[${indexStep}].step.stepComponents`, stepComponents)
  } else {
    setFieldValue(`productionSteps[${indexStep}].stepComponents`, stepComponents)
  }
  const { name } = parentStep // KFC-2429 - To be removed
  const updateParams = {
    currentStepComponent: stepComponentToDelete,
    currentStepName: name, // KFC-2429 - To be removed
    // currentStepComponentIndexes: stepComponentIndexes, KFC-2429 - To be added
    currentStepIndex: indexStep,
    allSteps: steps,
    section,
    recipe,
    method: "DELETE",
    isProductionSteps: !!fromRecipe
  }

  updatePriorStepsInUlteriorComponents(updateParams)
  if (isPseudoDeletion) {
    updatePriorStepsInUlteriorComponents({
      ...updateParams,
      currentStepComponent: emptyStepComponent,
      method: "CREATE"
    })
  }

  updateAllRecipesFieldsOnChange({
    recipe,
    section,
    steps,
    parentStep,
    currentStepComponentChanged: stepComponentToDelete,
    isProductionSteps: fromRecipe,
  })

}

export const updateStepComponent = (recipe, stepComponentToUpdate, setFieldValue, defaultStepComponents, defaultOptions) => {
  const options = { fromRecipe: true, ...(defaultOptions ?? {}) }
  const path = options.fromRecipe ? "sections.productionSteps.step.stepComponents.index" : "productionSteps.stepComponents.index"
  const [stepComponentInRecipe] = findObjectByPathValue(recipe, path, stepComponentToUpdate.index, { path: true, relative: !options.fromRecipe })
  const [componentIndex = options.index, indexStep = options.indexStep, sectionIndex = options.sectionIndex] = stepComponentInRecipe ? stepComponentInRecipe.path.match(/\d+/g).map(Number) : []
  const section = options.section ?? recipe.sections[sectionIndex]
  const parentStep = options.parentStep ?? recipe.sections[sectionIndex].productionSteps[indexStep].step
  const steps = options.steps ?? recipe.sections[sectionIndex].productionSteps
  const stepComponents = defaultStepComponents ?? parentStep.stepComponents
  const previousStepComponent = cloneDeep(stepComponents[componentIndex])
  stepComponentToUpdate = recalculateCostValues(stepComponentToUpdate)
  stepComponents[componentIndex] = stepComponentToUpdate
  if (options.fromRecipe) {
    setFieldValue(`sections[${sectionIndex}].productionSteps[${indexStep}].step.stepComponents`, stepComponents)
  } else {
    setFieldValue(`productionSteps[${indexStep}].stepComponents`, stepComponents)
  }

  const productionStepFieldName = options.fromRecipe
    ? `sections[${sectionIndex}].productionSteps[${indexStep}]`
    : `productionSteps[${indexStep}]`

  // Compute the root productionStep net/gross weight
  const totalNetWeight = parentStep.stepComponents.reduce((sum, comp) => sum + (comp.netWeight || 0), 0)
  const totalGrossWeight = parentStep.stepComponents.reduce((sum, comp) => sum + (comp.grossWeight || 0), 0)
  // Update the productionStep
  setFieldValue(`${productionStepFieldName}.netWeight`, totalNetWeight)
  setFieldValue(`${productionStepFieldName}.grossWeight`, totalGrossWeight)
  setFieldValue(`${productionStepFieldName}.step.netWeight`, totalNetWeight)
  setFieldValue(`${productionStepFieldName}.step.grossWeight`, totalGrossWeight)
  if (stepComponentToUpdate?.sectionOutput) {

    const haveDeleted = previousStepComponent.sectionOutput.stepComponents.some((prevSC) => {
      const isDeleted = !stepComponentToUpdate.sectionOutput.stepComponents.find(psc => psc.stepComponentIndex === prevSC.stepComponentIndex)
      if (isDeleted) {
        updatePriorStepsInUlteriorComponents({
          currentStepComponent: prevSC,
          currentStepName: parentStep.name,
          currentStepIndex: indexStep,
          allSteps: steps,
          section,
          recipe,
          method: "DELETE",
          isProductionSteps: !!options.fromRecipe
        })
      }
      return isDeleted
    })

    stepComponentToUpdate.sectionOutput.stepComponents.forEach((sC) => {
      const isNew = !previousStepComponent.sectionOutput.stepComponents.some(psc => psc.stepComponentIndex === sC.stepComponentIndex)
      if (haveDeleted || isNew) {
        updatePriorStepsInUlteriorComponents({
          currentStepComponent: sC,
          currentStepName: parentStep.name,
          currentStepIndex: indexStep,
          allSteps: steps,
          section,
          recipe,
          method: "CREATE",
          isProductionSteps: !!options.fromRecipe
        })
      }
      updateAllRecipesFieldsOnChange({
        recipe,
        section,
        steps,
        parentStep,
        currentStepComponentChanged: sC,
        isProductionSteps: options.fromRecipe,
      })
    })
  }
}